import { createConfig } from '@platform-shared/config';
import React, { createContext, useContext, FC, PropsWithChildren, useEffect } from 'react';

// Import the smarter-logging package
// Using default import for ESM compatibility with CommonJS modules
import UILogging from '@smarterservices/smarter-logging';

// Define label types for better type safety
export type LogLabel = string;

/**
 * Platform Logger Interface
 * Defines the shape of the logger functions and properties
 */
// Define log levels in order of severity
export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3
}

// Type for string log level values
export type LogLevelString = 'error' | 'warn' | 'info' | 'debug';

// Type for log destination
export type LogDestination = 'console' | 'remote' | 'both';

// Helper to convert string log level to enum value
export const stringToLogLevel = (level: LogLevelString): LogLevel => {
  switch (level.toLowerCase()) {
    case 'error': return LogLevel.ERROR;
    case 'warn': return LogLevel.WARN;
    case 'info': return LogLevel.INFO;
    case 'debug': return LogLevel.DEBUG;
    default: return LogLevel.ERROR;
  }
};

// Helper to convert enum log level to string
export const logLevelToString = (level: LogLevel): LogLevelString => {
  switch (level) {
    case LogLevel.ERROR: return 'error';
    case LogLevel.WARN: return 'warn';
    case LogLevel.INFO: return 'info';
    case LogLevel.DEBUG: return 'debug';
    default: return 'error';
  }
}

export interface PlatformLogger {
  // Logger state
  isInitialized: () => boolean;
  getLabels: () => Record<string, LogLabel>;
  getMetadata: () => Record<string, any>;
  getLogLevel: () => LogLevel;
  getLogLevelString: () => LogLevelString;
  getLogDestination: () => LogDestination;
  
  // Initialization
  initialize: (appName: string, customLabels?: Record<string, string>, metadata?: Record<string, any>, logLevel?: LogLevel | LogLevelString, logDestination?: LogDestination) => void;
  setLogLevel: (level: LogLevel | LogLevelString) => void;
  setLogDestination: (destination: LogDestination) => void;
  
  // Basic logging methods
  info: (label: LogLabel | null, message: string, object?: any, metadata?: Record<string, any>) => void;
  error: (label: LogLabel | null, message: string, errorObject?: any, metadata?: Record<string, any>) => void;
  warn: (label: LogLabel | null, message: string, object?: any, metadata?: Record<string, any>) => void;
  debug: (label: LogLabel | null, message: string, object?: any, metadata?: Record<string, any>) => void;
  
  // Context-aware logging methods
  logInfo: (context: string, label: LogLabel | null, message: string, object?: any, metadata?: Record<string, any>) => void;
  logError: (context: string, label: LogLabel | null, message: string, errorObject?: any, metadata?: Record<string, any>) => void;
  logWarn: (context: string, label: LogLabel | null, message: string, object?: any, metadata?: Record<string, any>) => void;
  logDebug: (context: string, label: LogLabel | null, message: string, object?: any, metadata?: Record<string, any>) => void;
  
  // Helper methods
  addLabels: (newLabels: Record<string, string>) => void;
  setMetadata: (metadata: Record<string, any>) => void;
}

/**
 * Create a platform logger instance
 * Uses a functional approach with closure to maintain state
 */
const createPlatformLogger = (): PlatformLogger => {
  // Private state
  let initialized = false;
  let labels: Record<string, LogLabel> = { DEFAULT: 'Generic' };
  let metadata: Record<string, any> = {};
  let logLevel: LogLevel = LogLevel.ERROR; // Default to ERROR level
  let logDestination: LogDestination = 'both'; // Default to both console and remote logging
  
  // Check if logger is initialized
  const checkInitialized = () => {
    if (!initialized) {
      console.warn('Logger not initialized. Call initialize() first.');
      return false;
    }
    return true;
  };
  
  // The logger instance with all methods
  const logger: PlatformLogger = {
    // State accessors
    isInitialized: () => initialized,
    getLabels: () => ({ ...labels }),
    getMetadata: () => ({ ...metadata }),
    getLogLevel: () => logLevel,
    getLogLevelString: () => logLevelToString(logLevel),
    getLogDestination: () => logDestination,
    
    // Initialize the logger with config from shared library
    initialize: (appName: string, customLabels = {}, customMetadata = {}, level = LogLevel.ERROR, destination: LogDestination = 'both') => {
      // Set the log level, handling both enum and string values
      if (typeof level === 'string') {
        logLevel = stringToLogLevel(level as LogLevelString);
      } else {
        logLevel = level as LogLevel;
      }
      
      // Set the log destination
      logDestination = destination;
      
      try {
        const config = createConfig(import.meta.env['VITE_MODE']);
        
        // Check if logging configuration exists
        if (config['logging'] && config['logging']['betterStack']) {
          // Get BetterStack config from the shared config
          const { token, host } = config['logging']['betterStack'];
          
          // Initialize the logger with the config
          // UILogging.init expects token, host, and optionally appName and metadata
          // Combine labels into metadata to fit the expected parameter structure
          const combinedMetadata = {
            ...customMetadata,
            labels: customLabels
          };
          UILogging.init(token, host, appName, combinedMetadata);
          
          // Update local state
          initialized = true;
          labels = { ...labels, ...customLabels };
          metadata = { ...metadata, ...customMetadata };
      
        } else {
          // Handle missing logging configuration
          console.warn('Logging configuration not found. Using console fallback.');
          
          // Still mark as initialized but will use console fallbacks
          initialized = true;
          labels = { ...labels, ...customLabels };
          metadata = { ...metadata, ...customMetadata };
        }
      } catch (error) {
        console.error('Failed to initialize platform logger:', error);
      }
    },
    
    // Basic logging methods that proxy to the smarter-logging package
    info: (label, message, object, customMetadata) => {
      if (!checkInitialized()) return;
      // Check if the current log level allows INFO messages
      if (logLevel < LogLevel.INFO) return;
      // Combine object and metadata to match the TypeScript definition (3 params)
      const combinedObject = customMetadata ? { data: object || null, metadata: customMetadata } : object || null;
      
      // Log based on destination setting
      if (logDestination === 'console' || logDestination === 'both') {
        console.info(`[${label || 'INFO'}] ${message}`, object || '');
      }
      if (logDestination === 'remote' || logDestination === 'both') {
        UILogging.info(label, message, combinedObject);
      }
    },
    
    error: (label, message, errorObject, customMetadata) => {
      if (!checkInitialized()) return;
      // ERROR level is always logged regardless of log level setting
      // Combine error object and metadata to match the TypeScript definition (3 params)
      const combinedObject = customMetadata ? { error: errorObject || null, metadata: customMetadata } : errorObject || null;
      
      // Log based on destination setting
      if (logDestination === 'console' || logDestination === 'both') {
        console.error(`[${label || 'ERROR'}] ${message}`, errorObject || '');
      }
      if (logDestination === 'remote' || logDestination === 'both') {
        UILogging.error(label, message, combinedObject);
      }
    },
    
    warn: (label, message, object, customMetadata) => {
      if (!checkInitialized()) return;
      // Check if the current log level allows WARN messages
      if (logLevel < LogLevel.WARN) return;
      // Combine object and metadata to match the TypeScript definition (3 params)
      const combinedObject = customMetadata ? { data: object || null, metadata: customMetadata } : object || null;
      
      // Log based on destination setting
      if (logDestination === 'console' || logDestination === 'both') {
        console.warn(`[${label || 'WARN'}] ${message}`, object || '');
      }
      if (logDestination === 'remote' || logDestination === 'both') {
        UILogging.warn(label, message, combinedObject);
      }
    },
    
    debug: (label, message, object, customMetadata) => {
      if (!checkInitialized()) return;
      // Check if the current log level allows DEBUG messages
      if (logLevel < LogLevel.DEBUG) return;
      // Combine object and metadata to match the TypeScript definition (3 params)
      const combinedObject = customMetadata ? { data: object || null, metadata: customMetadata } : object || null;
      
      // Log based on destination setting
      if (logDestination === 'console' || logDestination === 'both') {
        console.debug(`[${label || 'DEBUG'}] ${message}`, object || '');
      }
      if (logDestination === 'remote' || logDestination === 'both') {
        UILogging.debug(label, message, combinedObject);
      }
    },
    
    // Context-aware logging methods that add component/context information
    logInfo: (context, label, message, object, customMetadata) => {
      if (!checkInitialized()) return;
      // Check if the current log level allows INFO messages
      if (logLevel < LogLevel.INFO) return;
      const contextMessage = `[${context}] ${message}`;
      // Combine object and metadata to match the TypeScript definition (3 params)
      const combinedObject = customMetadata ? { data: object || null, metadata: customMetadata } : object || null;
      
      // Log based on destination setting
      if (logDestination === 'console' || logDestination === 'both') {
        console.info(`[${label || 'INFO'}] ${contextMessage}`, object || '');
      }
      if (logDestination === 'remote' || logDestination === 'both') {
        UILogging.info(label, contextMessage, combinedObject);
      }
    },
    
    logError: (context, label, message, errorObject, customMetadata) => {
      if (!checkInitialized()) return;
      // ERROR level is always logged regardless of log level setting
      const contextMessage = `[${context}] ${message}`;
      // Combine error object and metadata to match the TypeScript definition (3 params)
      const combinedObject = customMetadata ? { error: errorObject || null, metadata: customMetadata } : errorObject || null;
      
      // Log based on destination setting
      if (logDestination === 'console' || logDestination === 'both') {
        console.error(`[${label || 'ERROR'}] ${contextMessage}`, errorObject || '');
      }
      if (logDestination === 'remote' || logDestination === 'both') {
        UILogging.error(label, contextMessage, combinedObject);
      }
    },
    
    logWarn: (context, label, message, object, customMetadata) => {
      if (!checkInitialized()) return;
      // Check if the current log level allows WARN messages
      if (logLevel < LogLevel.WARN) return;
      const contextMessage = `[${context}] ${message}`;
      // Combine object and metadata to match the TypeScript definition (3 params)
      const combinedObject = customMetadata ? { data: object || null, metadata: customMetadata } : object || null;
      
      // Log based on destination setting
      if (logDestination === 'console' || logDestination === 'both') {
        console.warn(`[${label || 'WARN'}] ${contextMessage}`, object || '');
      }
      if (logDestination === 'remote' || logDestination === 'both') {
        UILogging.warn(label, contextMessage, combinedObject);
      }
    },
    
    logDebug: (context, label, message, object, customMetadata) => {
      if (!checkInitialized()) return;
      // Check if the current log level allows DEBUG messages
      if (logLevel < LogLevel.DEBUG) return;
      const contextMessage = `[${context}] ${message}`;
      // Combine object and metadata to match the TypeScript definition (3 params)
      const combinedObject = customMetadata ? { data: object || null, metadata: customMetadata } : object || null;
      
      // Log based on destination setting
      if (logDestination === 'console' || logDestination === 'both') {
        console.debug(`[${label || 'DEBUG'}] ${contextMessage}`, object || '');
      }
      if (logDestination === 'remote' || logDestination === 'both') {
        UILogging.debug(label, contextMessage, combinedObject);
      }
    },
    
    // Set the log level
    setLogLevel: (level: LogLevel | LogLevelString) => {
      // Handle both enum and string values
      if (typeof level === 'string') {
        logLevel = stringToLogLevel(level as LogLevelString);
      } else {
        logLevel = level as LogLevel;
      }
    },
    
    // Set the log destination
    setLogDestination: (destination: LogDestination) => {
      logDestination = destination;
    },
    
    // Helper methods
    addLabels: (newLabels) => {
      labels = { ...labels, ...newLabels };
      
      // Update the logger's labels if initialized
      if (initialized) {
        Object.assign(UILogging.labels, newLabels);
      }
    },
    
    setMetadata: (newMetadata) => {
      metadata = { ...metadata, ...newMetadata };
      
      // Update the logger's global metadata if initialized
      if (initialized) {
        // Use type assertion to tell TypeScript this method exists
        (UILogging as any).setGlobalMetadata(newMetadata);
      }
    }
  };
  
  return logger;
};

// Create a singleton instance
const platformLogger = createPlatformLogger();

// Create a React context for the logger
export const LoggerContext = createContext<PlatformLogger>(platformLogger);

/**
 * Hook to use the platform logger within components
 * @returns The platform logger instance
 */
export const useLogger = (): PlatformLogger => {
  return useContext(LoggerContext);
};

/**
 * Logger Provider Props Interface
 */
interface LoggerProviderProps {
  appName: string;
  labels?: Record<string, string>;
  metadata?: Record<string, any>;
  logLevel?: LogLevel | LogLevelString;
  logDestination?: LogDestination;
}

/**
 * Provider component that initializes the platform logger
 * This should be placed high in the component tree, typically near the root
 */
export const LoggerProvider: FC<PropsWithChildren<LoggerProviderProps>> = ({
  children,
  appName,
  labels = {
    AUTH: 'Authentication',
    UI: 'UserInterface',
    API: 'APIRequests',
    SESSION: 'SessionManagement',
    NAVIGATION: 'Navigation',
    ERROR: 'ErrorBoundary'
  },
  metadata = {
    environment: process.env['NODE_ENV'] || 'local',
    version: process.env['VITE_APP_VERSION'] || '1.0.0'
  },
  logLevel = LogLevel.ERROR,
  logDestination = 'both'
}) => {
  // Initialize the logger on mount
  useEffect(() => {
    if (!platformLogger.isInitialized()) {
      platformLogger.initialize(appName, labels, metadata, logLevel, logDestination);
    } else {
      // If already initialized, update the log level and destination
      platformLogger.setLogLevel(logLevel);
      platformLogger.setLogDestination(logDestination);
    }
  }, [appName, labels, metadata, logLevel, logDestination]);
  
  // Provide the logger instance to all children via context
  return (
    // Using the context provider with explicit typing
    React.createElement(LoggerContext.Provider, { value: platformLogger }, children)
  );
};

// Export the logger instance
export default platformLogger;
